<template>
  <div class="frequencia">
    <div v-if="loading" class="loading">
      <i class="fas fa-spinner fa-spin"></i> Carregando indicadores de frequência...
    </div>

    <template v-else>
      <el-row :gutter="10" class="u-mt-10">
        <el-col :xs="24" :sm="12" :md="6">
          <card-kpi
            label="% Presença Geral"
            :value="dados.perc_presenca_geral"
            :decimal="1"
            suffix="%"
            icon="fas fa-percentage"
            :primary="dados.perc_presenca_geral >= 75"
            :warn="dados.perc_presenca_geral > 0 && dados.perc_presenca_geral < 75 && dados.perc_presenca_geral >= 50"
            :danger="dados.perc_presenca_geral > 0 && dados.perc_presenca_geral < 50"
          />
        </el-col>
        <el-col :xs="24" :sm="12" :md="6">
          <card-kpi
            label="Alunos em risco (>25% faltas)"
            :value="dados.alunos_em_risco.length"
            icon="fas fa-exclamation-triangle"
            :danger="dados.alunos_em_risco.length > 0"
          />
        </el-col>
        <el-col :xs="24" :sm="12" :md="6">
          <card-kpi
            label="Cobertura de lançamento"
            :value="dados.cobertura_lancamento.perc"
            :decimal="1"
            suffix="%"
            :hint="`${formatNumber(dados.cobertura_lancamento.lancado)} / ${formatNumber(dados.cobertura_lancamento.esperado)} células`"
            icon="fas fa-clipboard-check"
            :warn="dados.cobertura_lancamento.perc > 0 && dados.cobertura_lancamento.perc < 70"
          />
        </el-col>
        <el-col :xs="24" :sm="12" :md="6">
          <card-kpi
            label="Meses com registros"
            :value="dados.evolucao_mensal.length"
            icon="fas fa-calendar-alt"
          />
        </el-col>
      </el-row>

      <el-row :gutter="10" class="u-mt-15">
        <el-col :xs="24" :sm="24" :md="8">
          <c-panel title="Presença × Faltas" titleIcon="fas fa-chart-pie">
            <div class="chart-box chart-box--md">
              <doughnut-chart class="chart-canvas" :chart-data="chartPresenca" />
            </div>
          </c-panel>
        </el-col>
        <el-col :xs="24" :sm="24" :md="16">
          <c-panel title="Evolução mensal da presença (%)" titleIcon="fas fa-chart-line">
            <div class="chart-box chart-box--md">
              <line-chart
                v-if="chartEvolucao.labels.length > 0"
                class="chart-canvas"
                :chart-data="chartEvolucao"
              />
              <div v-else class="empty">
                <el-empty description="Sem registros mensais no período" :image-size="80" />
              </div>
            </div>
          </c-panel>
        </el-col>
      </el-row>

      <el-row :gutter="10" class="u-mt-15">
        <el-col :xs="24" :sm="24" :md="24">
          <c-panel
            :title="`Cobertura por turma (Top ${dados.cobertura_lancamento.por_turma.length} mais críticas)`"
            titleIcon="fas fa-chart-bar"
          >
            <div class="chart-box chart-box--lg">
              <bar-chart
                v-if="chartCoberturaTurma.labels.length > 0"
                class="chart-canvas"
                :chart-data="chartCoberturaTurma"
              />
              <div v-else class="empty">
                <el-empty description="Sem dados de cobertura" :image-size="80" />
              </div>
            </div>
          </c-panel>
        </el-col>
      </el-row>

      <el-row :gutter="10" class="u-mt-15">
        <el-col :xs="24" :sm="24" :md="24">
          <c-panel
            :title="`Alunos em risco — Top ${dados.alunos_em_risco.length} com maior % de faltas`"
            titleIcon="fas fa-user-times"
          >
            <el-table
              v-if="dados.alunos_em_risco.length > 0"
              :data="dados.alunos_em_risco"
              size="small"
              style="width: 100%"
              :default-sort="{ prop: 'perc_faltas', order: 'descending' }"
              max-height="420"
            >
              <el-table-column prop="nome" label="Aluno" min-width="220" sortable />
              <el-table-column prop="turma" label="Turma" min-width="200" sortable />
              <el-table-column prop="total" label="Total aulas" width="120" align="right" sortable />
              <el-table-column prop="faltas" label="Faltas" width="100" align="right" sortable />
              <el-table-column prop="perc_faltas" label="% Faltas" width="120" align="right" sortable>
                <template slot-scope="scope">
                  <span :class="['perc-pill', percClass(scope.row.perc_faltas)]">
                    {{ formatPerc(scope.row.perc_faltas) }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
            <el-empty v-else description="Nenhum aluno em risco no período" :image-size="80" />
          </c-panel>
        </el-col>
      </el-row>

      <div class="atualizado-em" v-if="dados.atualizado_em">
        <i class="fas fa-clock"></i>
        Atualizado em {{ formatDateTime(dados.atualizado_em) }}
        <span class="cache-hint">(cache de 5 min)</span>
      </div>
    </template>
  </div>
</template>

<script>
const MESES_LABELS = {
  '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr',
  '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago',
  '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez'
}

export default {
  name: 'FrequenciaTab',
  components: {
    CardKpi: () => import('../shared/CardKpi.vue'),
    cPanel: () => import('@/shared/c-panel/c-panel.vue'),
    DoughnutChart: () => import('@/chart/DoughnutChart'),
    LineChart: () => import('@/chart/LineChart'),
    BarChart: () => import('@/chart/BarChart')
  },
  props: {
    periodoId: { type: [Number, String], default: null },
    instituicaoId: { type: [Number, String], default: null },
    municipal: { type: Boolean, default: false }
  },
  data: () => ({
    loading: false,
    dados: {
      perc_presenca_geral: 0,
      alunos_em_risco: [],
      evolucao_mensal: [],
      cobertura_lancamento: { perc: 0, esperado: 0, lancado: 0, por_turma: [] },
      atualizado_em: null
    }
  }),
  computed: {
    chartPresenca () {
      const p = Number(this.dados.perc_presenca_geral || 0)
      const f = Math.max(0, Math.round((100 - p) * 10) / 10)
      return {
        labels: ['Presenças', 'Faltas'],
        datasets: [{ data: [p, f], backgroundColor: ['#50964d', '#d0021b'] }]
      }
    },
    chartEvolucao () {
      const lista = this.dados.evolucao_mensal || []
      return {
        labels: lista.map(it => this.formatMes(it.mes)),
        datasets: [{
          label: '% Presença',
          data: lista.map(it => Number(it.perc_presenca || 0)),
          borderColor: '#346daa',
          backgroundColor: 'rgba(52,109,170,0.15)',
          fill: true,
          tension: 0.3
        }]
      }
    },
    chartCoberturaTurma () {
      const lista = this.dados.cobertura_lancamento.por_turma || []
      return {
        labels: lista.map(it => this.truncate(it.turma, 35)),
        datasets: [{
          label: 'Cobertura (%)',
          data: lista.map(it => Number(it.perc || 0)),
          backgroundColor: lista.map(it => {
            const p = Number(it.perc || 0)
            if (p >= 75) return '#50964d'
            if (p >= 50) return '#f5a623'
            return '#d0021b'
          })
        }]
      }
    }
  },
  watch: {
    periodoId () { this.load() },
    instituicaoId () { this.load() },
    municipal () { this.load() }
  },
  mounted () {
    this.load()
  },
  methods: {
    load () {
      if (!this.periodoId) return
      if (!this.municipal && !this.instituicaoId) return

      this.loading = true
      const base = this.$store.state.config.baseUrlApi
      let url
      let payload = {}

      if (this.municipal) {
        url = `${base}/api/dashboard-diario/municipal/frequencia/${this.periodoId}`
        if (this.instituicaoId) payload.instituicao_id = this.instituicaoId
      } else {
        url = `${base}/api/dashboard-diario/frequencia/${this.instituicaoId}/${this.periodoId}`
      }

      this.$axios.post(url, payload, this.$store.getters.getHeaderAuth)
        .then(response => {
          this.dados = Object.assign({}, this.dados, response.data || {})
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$store.commit('throwException', error)
        })
    },
    formatMes (yyyymm) {
      if (!yyyymm) return ''
      const parts = String(yyyymm).split('-')
      if (parts.length !== 2) return yyyymm
      return `${MESES_LABELS[parts[1]] || parts[1]}/${parts[0].slice(-2)}`
    },
    formatNumber (v) {
      return Number(v || 0).toLocaleString('pt-BR')
    },
    formatPerc (v) {
      return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%'
    },
    percClass (v) {
      const n = Number(v || 0)
      if (n >= 50) return 'perc-pill--danger'
      if (n >= 35) return 'perc-pill--warn'
      return 'perc-pill--neutral'
    },
    truncate (str, len) {
      if (!str) return ''
      return str.length > len ? str.slice(0, len - 1) + '…' : str
    },
    formatDateTime (iso) {
      if (!iso) return ''
      try { return this.$moment(iso).format('DD/MM/YYYY HH:mm') } catch (e) { return iso }
    }
  }
}
</script>

<style scoped>
.chart-box { position: relative; width: 100%; overflow: hidden; height: 280px; }
.chart-box--lg { height: 380px; }
.chart-canvas { display: block; width: 100% !important; height: 100% !important; }
.chart-box ::v-deep canvas { width: 100% !important; height: 100% !important; }
.empty {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
}
.loading {
  padding: 40px 0;
  text-align: center;
  color: rgba(0, 0, 0, 0.45);
}
.loading i { margin-right: 6px; color: #346daa; }
.atualizado-em {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  text-align: right;
  margin-top: 12px;
  padding-right: 6px;
}
.cache-hint { color: rgba(0, 0, 0, 0.30); margin-left: 4px; }
.perc-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}
.perc-pill--neutral { background: rgba(0, 0, 0, 0.06); color: rgba(0, 0, 0, 0.65); }
.perc-pill--warn { background: rgba(245, 166, 35, 0.18); color: #b07810; }
.perc-pill--danger { background: rgba(208, 2, 27, 0.18); color: #a30217; }
</style>
